(() => {
  if (customElements.get('ptc-hat-intro')) return;
  let completedForPage = false;
  const clamp = (n, min = 0, max = 1) => Math.min(max, Math.max(min, n));
  const smooth = (a, b, n) => { const x = clamp((n - a) / (b - a)); return x * x * (3 - 2 * x); };

  class PTCHatIntro extends HTMLElement {
    connectedCallback() {
      // Deferring also supports insertion/replacement by Shopify's section editor.
      this.setupFrame = requestAnimationFrame(() => this.setup());
    }

    setup() {
      if (!this.isConnected || this.controller) return;
      this.section = this.closest('.shopify-section') || this.parentElement;
      if (completedForPage) {
        this.section.hidden = true;
        return;
      }
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.following = this.section.nextElementSibling;
      this.destination = this.section.querySelector('.ptc-hat-intro__destination');
      this.viewport = this.querySelector('.ptc-hat-intro__viewport');
      this.counter = this.querySelector('.ptc-hat-intro__counter');
      this.image = this.querySelector('img');
      this.imageFailed = this.image.complete && this.image.naturalWidth === 0;
      this.screenHeight = this.viewport.getBoundingClientRect().height;
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.smallQuery = matchMedia('(max-width: 749px)');
      this.target = this.current = 0;
      this.frame = 0;
      this.lastTime = 0;
      this.draw = this.draw.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.measure = this.measure.bind(this);
      this.configure = this.configure.bind(this);
      this.motionQuery.addEventListener('change', this.configure, { signal });
      this.smallQuery.addEventListener('change', this.measure, { signal });
      window.addEventListener('resize', this.measure, { passive: true, signal });
      window.addEventListener('scroll', this.onScroll, { passive: true, signal });
      window.addEventListener('pageshow', this.measure, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) { cancelAnimationFrame(this.frame); this.frame = 0; }
        else this.onScroll();
      }, { signal });
      this.querySelectorAll('a').forEach(link => link.addEventListener('click', event => this.skip(event), { signal }));
      this.image.addEventListener('error', () => {
        this.imageFailed = true;
        this.configure();
      }, { signal });
      this.image.addEventListener('load', this.measure, { signal });
      this.configure();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.measure);
        const header = document.querySelector('#header-group');
        if (header) this.resizeObserver.observe(header);
      }
    }

    configure() {
      if (this.completed) return;
      // A static hero is the default; never trap the customer in an empty scroll sequence.
      this.animated = this.dataset.motion !== 'false' && !this.motionQuery.matches && !this.imageFailed;
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.classList.toggle('is-animated', this.animated);
      this.classList.remove('is-finished');
      this.following?.classList.toggle('ptc-intro-following', this.animated);
      if (!this.animated) {
        this.following?.style.removeProperty('--ptc-intro-overlap');
        this.current = this.target = 0;
        this.paint(0);
      }
      this.measure();
    }

    measure() {
      if (!this.isConnected || this.completed) return;
      const header = document.querySelector('#header-group');
      // Use the actual header size; the theme can change it between desktop and mobile.
      this.top = Math.min(header?.getBoundingClientRect().height || 0, window.innerHeight * .3);
      this.style.setProperty('--intro-top', `${this.top}px`);
      this.style.setProperty('--intro-screen', `calc(100svh - ${this.top}px)`);
      const distance = clamp(Number(this.dataset.distance) || 200, 120, 300);
      this.style.setProperty('--intro-travel', `${this.smallQuery.matches ? distance * .8 : distance}svh`);
      this.screenHeight = this.viewport.getBoundingClientRect().height;
      this.travel = Math.max(1, this.offsetHeight - this.screenHeight);
      if (this.animated) this.following?.style.setProperty('--ptc-intro-overlap', `${this.screenHeight}px`);
      this.onScroll();
    }

    onScroll() {
      if (!this.animated || document.hidden) return;
      this.target = clamp((this.top - this.getBoundingClientRect().top) / this.travel);
      if (!this.frame) this.frame = requestAnimationFrame(this.draw);
    }

    draw(time) {
      this.frame = 0;
      if (!this.animated || !this.isConnected) return;
      const dt = this.lastTime ? Math.min(time - this.lastTime, 64) : 16;
      this.lastTime = time;
      this.current += (this.target - this.current) * (1 - Math.exp(-dt / 65));
      if (Math.abs(this.target - this.current) < .0003) this.current = this.target;
      this.paint(this.current);
      if (!this.completed && this.current !== this.target) this.frame = requestAnimationFrame(this.draw);
    }

    paint(p) {
      const turn = smooth(0, .62, p);
      const close = smooth(.48, 1, p);
      const leave = smooth(.8, .985, p);
      const mobile = this.smallQuery.matches;
      const fly = 1 - Math.pow(1 - clamp(p / .14), 3);
      const values = {
        '--intro-turn': `${-12 + 27 * turn - 22 * close}deg`,
        '--intro-yaw': `${-19 + 38 * turn - 21 * close}deg`,
        '--intro-pitch': `${7 - 12 * turn + 4 * close}deg`,
        '--intro-scale': .9 + .18 * turn + (mobile ? 1.9 : 2.35) * close,
        '--intro-x': `${Math.sin(p * Math.PI * 2) * (mobile ? 13 : 42)}px`,
        '--intro-y': `${-12 * turn - close * this.screenHeight * .18}px`,
        '--intro-fade': 1 - leave,
        '--intro-type-fade': 1 - (mobile ? smooth(.05, .14, p) : smooth(.10, .22, p)),
        '--intro-type-scale': mobile ? 1 : 1 - .86 * smooth(0, .22, p),
        '--intro-type-flight': `${mobile ? fly * (this.screenHeight * .65 + 120) : 0}px`,
        '--intro-detail-one': smooth(.12, .26, p) * (1 - smooth(.36, .49, p)),
        '--intro-detail-two': smooth(.38, .49, p) * (1 - smooth(.6, .72, p)),
        '--intro-shadow': .15 * (1 - smooth(.15, .7, p)),
        '--intro-shadow-scale': 1 - p * .4,
        '--intro-progress': p
      };
      for (const [key, value] of Object.entries(values)) this.style.setProperty(key, value);
      const step = p < .3 ? '01' : p < .68 ? '02' : '03';
      if (this.step !== step && this.counter) {
        this.counter.replaceChildren(document.createTextNode(`${step} `), Object.assign(document.createElement('span'), { textContent: '/' }), document.createTextNode(' 03'));
        this.step = step;
      }
      this.classList.toggle('is-finished', this.animated && p >= .985);
      if (this.animated && p >= .985) this.finish();
    }

    finish() {
      if (this.completed) return;
      this.completed = completedForPage = true;
      this.animated = false;
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      const before = this.following?.getBoundingClientRect().top;
      const focusedInside = this.section.contains(document.activeElement);
      this.section.hidden = true;
      this.following?.classList.remove('ptc-intro-following');
      this.following?.style.removeProperty('--ptc-intro-overlap');
      // Remove the spent scroll space while keeping the storefront in place.
      if (this.following) {
        window.scrollBy({ top: this.following.getBoundingClientRect().top - before, behavior: 'instant' });
        if (focusedInside) {
          if (!this.following.hasAttribute('tabindex')) {
            this.following.setAttribute('tabindex', '-1');
            this.addedTabindex = true;
          }
          this.following.focus({ preventScroll: true });
        }
      }
    }

    skip(event) {
      if (!this.following || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const y = window.scrollY + this.following.getBoundingClientRect().top - this.top;
      window.scrollTo({ top: Math.max(0, y), behavior: this.motionQuery.matches ? 'instant' : 'smooth' });
      // Focus the destination for keyboard and screen-reader users without undoing the scroll.
      if (!this.following.hasAttribute('tabindex')) {
        this.following.setAttribute('tabindex', '-1');
        this.addedTabindex = true;
      }
      this.following.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.setupFrame);
      cancelAnimationFrame(this.frame);
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.following?.classList.remove('ptc-intro-following');
      this.following?.style.removeProperty('--ptc-intro-overlap');
      if (this.addedTabindex) this.following?.removeAttribute('tabindex');
    }
  }
  customElements.define('ptc-hat-intro', PTCHatIntro);
})();
